/**
 * Date utility functions for formatting dates without timezone conversion.
 *
 * CRITICAL: When working with date strings like "1980-01-15", we must avoid timezone conversion
 * that can cause the date to shift by one day (e.g., "1980-01-15" becoming "1980-01-14").
 */

/**
 * Format an ISO date string (YYYY-MM-DD) or Date object for display without timezone conversion.
 *
 * Why this function exists:
 * - new Date("1980-01-15").toLocaleDateString() can show "1980-01-14" due to timezone offset
 * - This function preserves the exact date as written in the ISO string or Date object
 *
 * @param dateInput - Date string in ISO format (YYYY-MM-DD) or Date object
 * @param locale - Locale for formatting (default: 'fr-CA' for Quebec)
 * @param options - Intl.DateTimeFormat options (default: { year: 'numeric', month: 'long', day: 'numeric' })
 * @returns Formatted date string without timezone conversion
 *
 * @example
 * formatDateWithoutTimezone("1980-01-15", "fr-CA")
 * // Returns: "15 janvier 1980"
 *
 * @example
 * formatDateWithoutTimezone("1980-01-15", "en-CA")
 * // Returns: "January 15, 1980"
 *
 * @example
 * formatDateWithoutTimezone(new Date(1980, 0, 15), "fr-CA")
 * // Returns: "15 janvier 1980"
 */
export function formatDateWithoutTimezone(
  dateInput: string | Date | null | undefined,
  locale: string = 'fr-CA',
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }
): string {
  if (!dateInput || dateInput === 'N/A') return '';

  let year: number, month: number, day: number;

  // If it's a Date object, extract year, month, day from it
  if (dateInput instanceof Date) {
    year = dateInput.getFullYear();
    month = dateInput.getMonth() + 1; // getMonth() returns 0-11
    day = dateInput.getDate();
  } else {
    // Handle full ISO timestamp (YYYY-MM-DDTHH:mm:ss.sssZ) - extract just the date part
    let dateString = dateInput;
    if (dateString.includes('T')) {
      dateString = dateString.split('T')[0];
    }

    // Extract year, month, day from ISO string (YYYY-MM-DD)
    const parts = dateString.split('-').map(Number);
    [year, month, day] = parts;

    if (!year || !month || !day) {
      console.warn(`Invalid date format: ${dateInput}. Expected YYYY-MM-DD.`);
      return dateInput; // Return original if invalid
    }
  }

  // Create date in LOCAL timezone (not UTC) to avoid timezone shift
  // Using new Date(year, month - 1, day) creates a date at midnight in LOCAL time
  const date = new Date(year, month - 1, day);

  // Format using Intl.DateTimeFormat for localized output
  return new Intl.DateTimeFormat(locale, options).format(date);
}

/**
 * Format an ISO date string or Date object to short format (YYYY-MM-DD) without timezone conversion.
 *
 * @param dateInput - Date string in ISO format (YYYY-MM-DD) or Date object
 * @returns Date in YYYY-MM-DD format
 *
 * @example
 * formatDateShort("1980-01-15")
 * // Returns: "1980-01-15"
 *
 * @example
 * formatDateShort(new Date(1980, 0, 15))
 * // Returns: "1980-01-15"
 */
export function formatDateShort(dateInput: string | Date | null | undefined): string {
  if (!dateInput || dateInput === 'N/A') return '';

  // If it's a string, extract just the date part (YYYY-MM-DD)
  if (typeof dateInput === 'string') {
    // Handle full ISO timestamp (YYYY-MM-DDTHH:mm:ss.sssZ) - extract just the date part
    if (dateInput.includes('T')) {
      return dateInput.split('T')[0];
    }
    return dateInput;
  }

  // If it's a Date object, convert to YYYY-MM-DD using local timezone
  if (dateInput instanceof Date) {
    const year = dateInput.getFullYear();
    const month = String(dateInput.getMonth() + 1).padStart(2, '0');
    const day = String(dateInput.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  return '';
}

/**
 * Format an ISO date string or Date object to compact format (DD/MM/YYYY) without timezone conversion.
 *
 * @param dateInput - Date string in ISO format (YYYY-MM-DD) or Date object
 * @returns Date in DD/MM/YYYY format
 *
 * @example
 * formatDateCompact("1980-01-15")
 * // Returns: "15/01/1980"
 *
 * @example
 * formatDateCompact(new Date(1980, 0, 15))
 * // Returns: "15/01/1980"
 */
export function formatDateCompact(dateInput: string | Date | null | undefined): string {
  if (!dateInput || dateInput === 'N/A') return '';

  // Normalize to YYYY-MM-DD (strips ISO timestamp suffix if present)
  let dateString: string;
  if (dateInput instanceof Date) {
    dateString = formatDateShort(dateInput);
  } else {
    dateString = dateInput.includes('T') ? dateInput.split('T')[0] : dateInput;
  }

  // Handle empty string returned from formatDateShort
  if (!dateString) return '';

  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
}

/**
 * Format a date for display with fallback to "N/A" if date is missing or invalid.
 * Uses locale-aware formatting without timezone conversion.
 *
 * @param dateInput - Date string in ISO format (YYYY-MM-DD) or Date object
 * @param locale - Locale for formatting (default: 'fr-CA')
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string or "N/A" if invalid
 *
 * @example
 * formatDateOrNA("1980-01-15", "fr-CA")
 * // Returns: "15 janvier 1980"
 *
 * @example
 * formatDateOrNA(null)
 * // Returns: "N/A"
 */
export function formatDateOrNA(
  dateInput: string | Date | null | undefined,
  locale: string = 'fr-CA',
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }
): string {
  if (!dateInput) return 'N/A';

  const formatted = formatDateWithoutTimezone(dateInput, locale, options);
  return formatted || 'N/A';
}

/**
 * Format a date to short format (YYYY-MM-DD) with fallback to "N/A".
 *
 * @param dateInput - Date string in ISO format (YYYY-MM-DD) or Date object
 * @returns Date in YYYY-MM-DD format or "N/A"
 *
 * @example
 * formatDateShortOrNA("1980-01-15")
 * // Returns: "1980-01-15"
 *
 * @example
 * formatDateShortOrNA(null)
 * // Returns: "N/A"
 */
export function formatDateShortOrNA(dateInput: string | Date | null | undefined): string {
  if (!dateInput) return 'N/A';
  return formatDateShort(dateInput);
}

/**
 * Format a date to compact format (DD/MM/YYYY) with fallback to "N/A".
 *
 * @param dateInput - Date string in ISO format (YYYY-MM-DD) or Date object
 * @returns Date in DD/MM/YYYY format or "N/A"
 *
 * @example
 * formatDateCompactOrNA("1980-01-15")
 * // Returns: "15/01/1980"
 *
 * @example
 * formatDateCompactOrNA(null)
 * // Returns: "N/A"
 */
export function formatDateCompactOrNA(dateInput: string | Date | null | undefined): string {
  if (!dateInput) return 'N/A';
  return formatDateCompact(dateInput);
}

/**
 * Parse a date string (YYYY-MM-DD) as a local date to avoid timezone issues.
 * Using new Date(dateString) interprets the string as UTC, which can shift dates.
 */
export function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Number of calendar days between two dates, counting both endpoints
 * (a same-day trip counts as 1 day). Inputs are expected at local midnight.
 * Rounding (not flooring) absorbs the ±1h drift of DST transition days,
 * which would otherwise shave a day off windows spanning spring-forward.
 */
export function inclusiveDayCount(start: Date, end: Date): number {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  return Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;
}
